/* eslint-disable react/prop-types */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'components/ui/DatePicker';
import { intlShape } from 'react-intl';
import { stationsShape } from 'constants/propTypes';
import styled from 'styled-components';
import moment from 'moment';
import StationSelection from './StationSelection';
import { odAlreadySelected, setTileClassName } from './JourneySelection.utils';

const MultiCityContainer = styled('div')`
  width: 48%;
  min-width: 400px;
`;

const JourneySelection = ({
  destination,
  origin,
  outboundDate,
  multicity,
  inboundDate,
  returningDestination,
  returningOrigin,
  intl: { formatMessage },
  onJourneyChange,
  stations,
  swapOriginDestination,
  outboundDatePickerErrorMessage,
  inboundDatePickerErrorMessage,
  setDatePickerErrorMessage
}) => {
  const LayoutContainer = multicity ? MultiCityContainer : Fragment;
  const setTravelDate = (direction, date) => onJourneyChange({ [direction]: date });
  const clearTravelDate = (direction) => setTravelDate(direction, null);

  const sections = [
    {
      key: 'outbound',
      stationSelectionProps: {
        destinationValue: destination,
        originValue: origin,
        odAlreadySelected: odAlreadySelected(destination, origin),
        originOnChange: (e) => onJourneyChange({ origin: e || '' }),
        destinationOnChange: (e) => onJourneyChange({ destination: e || '' }),
        originKey: 'origin',
        destinationKey: 'destination',
        title: 'departing'
      },
      datePickerProps: {
        errorMessage: outboundDatePickerErrorMessage,
        setErrorMessage: (val) => setDatePickerErrorMessage('outboundDatePickerErrorMessage', val),
        id: 'outboundDate',
        className: 'left-date-picker',
        value: outboundDate,
        minDate: moment(new Date()),
        maxDate: inboundDate,
        setTravelDate,
        clearTravelDate,
        setTileClassName: ({ date, view }) => setTileClassName({ date, view, inboundDate, outboundDate })
      }
    },
    {
      key: 'inbound',
      hide: !multicity,
      stationSelectionProps: {
        destinationValue: returningDestination,
        originValue: returningOrigin,
        odAlreadySelected: odAlreadySelected(returningDestination, returningOrigin),
        originOnChange: (e) => onJourneyChange({ returningOrigin: e || '' }),
        destinationOnChange: (e) => onJourneyChange({ returningDestination: e || '' }),
        originKey: 'inboundOrigin',
        destinationKey: 'inboundDestination',
        title: 'returning'
      },
      datePickerProps: {
        activeStartDate: outboundDate,
        errorMessage: inboundDatePickerErrorMessage,
        setErrorMessage: (val) => setDatePickerErrorMessage('inboundDatePickerErrorMessage', val),
        id: 'inboundDate',
        className: 'right-date-picker',
        value: inboundDate,
        setTravelDate,
        clearTravelDate,
        minDate: outboundDate || moment(new Date()),
        setTileClassName: ({ date, view }) => setTileClassName({ date, view, inboundDate, outboundDate })
      }
    }
  ];

  return (
    <>
      {sections.map(({ stationSelectionProps, datePickerProps, hide, key }) => (
        <LayoutContainer key={key}>
          {!hide && (
            <StationSelection
              {...{
                ...stationSelectionProps,
                multicity,
                stations,
                swapOriginDestination
              }}
            />
          )}
          <DatePicker
            multicity={multicity}
            {...{
              ...datePickerProps,
              marginLeft: 10,
              label: multicity ? formatMessage({ id: 'bookingMagnet.on' }) : formatMessage({ id: 'bookingMagnet.departFrom' })
            }}
          />
        </LayoutContainer>
      ))}
    </>
  );
};

JourneySelection.propTypes = {
  destination: PropTypes.string,
  origin: PropTypes.string,
  multicity: PropTypes.bool,
  outboundDate: PropTypes.instanceOf(moment),
  inboundDate: PropTypes.instanceOf(moment),
  returningDestination: PropTypes.string,
  returningOrigin: PropTypes.string,
  intl: intlShape,
  onJourneyChange: PropTypes.func,
  stations: stationsShape,
  swapOriginDestination: PropTypes.func,
  outboundDatePickerErrorMessage: PropTypes.string,
  inboundDatePickerErrorMessage: PropTypes.string,
  setDatePickerErrorMessage: PropTypes.func
};

export default JourneySelection;
